<script module lang="ts">
    import type { HTMLInputAttributes } from 'svelte/elements';
    import type { Field } from '@/types/types';

    export type Props = {
        label?: string;
        error?: null | string;
        checked?: boolean;
        class?: string;
        field?: Field;
        args?: HTMLInputAttributes;
    };
</script>

<script lang="ts">
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label/index.js';
    import Checkbox from '@/components/ui/checkbox/checkbox.svelte';
    import { cn } from '@/lib/utils.js';
    import { createFieldValidation } from '@/lib/fieldValidation';
    import { onMount } from 'svelte';

    let {
        label,
        error = null,
        checked = $bindable(),
        class: className,
        field,
        ...args
    }: Props = $props();

    let isDirty = $state(false);

    const computedLabel = $derived(field?.label ?? label);

    // Get validation state - for checkboxes, value is true/false
    const validation = $derived.by(() => {
        return createFieldValidation({
            field,
            ui_type: field?.ui_type ?? 'checkbox',
            input_type: field?.input_type ?? undefined,
            value: checked ? 'checked' : '', // Convert boolean to string for validation
            isDirty,
            required: field?.required
        });
    });

    // Compute CSS classes - include server errors
    const validationClasses = $derived(() => {
        if (error) {
            return 'input-error';
        }
        return validation.classes;
    });

    // Mark as dirty when checkbox is clicked
    function handleChange() {
        isDirty = true;
    }
</script>

<div class={cn('flex-auto flex items-center gap-2', className)}>
    <Checkbox
        id={computedLabel}
        name={field?.column_name}
        bind:checked
        onCheckedChange={handleChange}
        class={validationClasses()}
        {...args}
    />
    <Label for={computedLabel}>
        {computedLabel}
        {#if validation.isRequired}
            <span class="ml-1 text-red-500">*</span>
        {/if}
    </Label>
</div>
{#if validation.errorMessage}
    <InputError class="mt-2" message={validation.errorMessage} />
{:else if error}
    <InputError class="mt-2" message={error} />
{/if}
